export const EASTERN_TZ = "America/New_York";

export const TIME_ZONE_CHOICES = [
  { id: "America/New_York", name: "Eastern Time" },
  { id: "America/Chicago", name: "Central Time" },
  { id: "America/Denver", name: "Mountain Time" },
  { id: "America/Phoenix", name: "Arizona Time" },
  { id: "America/Los_Angeles", name: "Pacific Time" },
  { id: "America/Anchorage", name: "Alaska Time" },
  { id: "Pacific/Honolulu", name: "Hawaii Time" },
] as const;

export function normalizeZone(timeZoneId?: string | null): string {
  const match = TIME_ZONE_CHOICES.find((z) => z.id === timeZoneId);
  return match?.id ?? EASTERN_TZ;
}

export function zoneDisplayName(timeZoneId?: string | null): string {
  return TIME_ZONE_CHOICES.find((z) => z.id === normalizeZone(timeZoneId))?.name ?? "Eastern Time";
}

export function parseUtcInstant(value: string): Date {
  const trimmed = value.trim();
  if (!trimmed) return new Date(NaN);
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return new Date(`${trimmed}T00:00:00Z`);
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
  return new Date(hasZone ? trimmed : `${trimmed}Z`);
}

export function zoneAbbrevAt(utcIso: string, timeZoneId?: string | null): string {
  const instant = parseUtcInstant(utcIso);
  if (Number.isNaN(instant.getTime())) return "";
  const zone = normalizeZone(timeZoneId);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    timeZoneName: "short",
  }).formatToParts(instant);
  const name = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  return /^[A-Z]{2,5}$/.test(name) ? name : zoneDisplayName(zone);
}

export function wallClockToUtcIso(dateStr: string, timeStr: string, timeZoneId?: string | null): string {
  const zone = normalizeZone(timeZoneId);
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [h, m] = timeStr.split(":").map(Number);
  const asUtc = Date.UTC(y, mo - 1, d, h, m, 0);
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const offsetMs = (instant: Date) => {
    const parts = Object.fromEntries(
      dtf.formatToParts(instant).filter((p) => p.type !== "literal").map((p) => [p.type, p.value])
    );
    return (
      Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second)
      ) - instant.getTime()
    );
  };
  const guess = new Date(asUtc);
  const offset = offsetMs(guess);
  let instant = new Date(asUtc - offset);
  const offset2 = offsetMs(instant);
  if (offset2 !== offset) instant = new Date(asUtc - offset2);
  return instant.toISOString();
}

export function formatInstantRange(
  startIso?: string | null,
  endIso?: string | null,
  timeZoneId?: string | null
): string {
  if (!startIso) return "Time TBD";
  const zone = normalizeZone(timeZoneId);
  const s = parseUtcInstant(startIso);
  if (Number.isNaN(s.getTime())) return startIso;
  const date = s.toLocaleDateString("en-US", {
    timeZone: zone,
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const t1 = s.toLocaleTimeString("en-US", {
    timeZone: zone,
    hour: "numeric",
    minute: "2-digit",
  });
  const abbr = zoneAbbrevAt(startIso, zone);
  if (!endIso) return `${date}, ${t1} ${abbr}`;
  const e = parseUtcInstant(endIso);
  const t2 = Number.isNaN(e.getTime())
    ? ""
    : e.toLocaleTimeString("en-US", {
        timeZone: zone,
        hour: "numeric",
        minute: "2-digit",
      });
  return t2 ? `${date}, ${t1} – ${t2} ${abbr}` : `${date}, ${t1} ${abbr}`;
}

export function formatInstantLong(iso: string, timeZoneId?: string | null): string {
  const zone = normalizeZone(timeZoneId);
  const d = parseUtcInstant(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const text = d.toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: zone,
  });
  return `${text} ${zoneAbbrevAt(iso, zone)}`;
}

/** Conference whose start date is closest to the booking date. */
export function nearestConference<T extends { id: number; eventDate?: string | null; timeZoneId?: string | null }>(
  conferences: T[],
  dateStr?: string | null
): T | null {
  const target = dateStr ? Date.parse(`${dateStr}T12:00:00Z`) : Date.now();
  let best: T | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const conference of conferences) {
    if (!conference.eventDate) continue;
    const day = conference.eventDate.includes("T")
      ? conference.eventDate.split("T")[0]
      : conference.eventDate;
    const when = Date.parse(`${day}T12:00:00Z`);
    if (Number.isNaN(when)) continue;
    const distance = Math.abs(when - target);
    if (distance < bestDistance) {
      best = conference;
      bestDistance = distance;
    }
  }
  return best;
}
